import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
    type UpdatePayGatePaymentModuleMutation,
    type UpdatePayGatePaymentModuleMutationVariables,
    UpdatePayGatePaymentModuleDocument,
} from '../../../../api/mutations/updatePayGatePaymentModule';
import { type PayGatePaymentModuleSettings } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

export type FormValues = PayGatePaymentModuleSettings;

type updatePayGatePaymentModuleFormProps = {
    moduleId: string;
    initialValues: FormValues;
    onComplete: () => void;
};

const loadingMessageKey = 'update-paygate-payment-module-loading';

const UpdatePayGatePaymentModuleForm = ({
    moduleId,
    initialValues,
    onComplete,
}: updatePayGatePaymentModuleFormProps) => {
    const { t } = useTranslation(['payGatePaymentModuleDetails']);

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('paygatePaymentModuleDetails:paymentModuleDrawer.messages.submitting'),
                key: loadingMessageKey,
                duration: 0,
            });

            await apolloClient
                .mutate<UpdatePayGatePaymentModuleMutation, UpdatePayGatePaymentModuleMutationVariables>({
                    mutation: UpdatePayGatePaymentModuleDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy(loadingMessageKey);
                });

            message.success({
                content: t('paygatePaymentModuleDetails:paymentModuleDrawer.messages.success'),
                key: 'primary',
            });

            // close the drawer
            onComplete();
        },
        [moduleId, apolloClient, onComplete, t]
    );

    const validate = useValidator(validators.requiredNonEmptyString('displayName'));

    return (
        <Formik<FormValues> initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="updatePayGatePaymentModule"
                    layout="vertical"
                    name="updatePayGatePaymentModule"
                    onSubmitCapture={handleSubmit}
                >
                    <InputField
                        {...t('paygatePaymentModuleDetails:paymentModuleDrawer.fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                </Form>
            )}
        </Formik>
    );
};

export default UpdatePayGatePaymentModuleForm;
